import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, MousePointerClick, ShieldAlert, ShoppingCart } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import {
  AnalyticsComparisonWindow,
  AnalyticsOverview,
  AnalyticsReliabilitySummary,
  compareMetricValues,
  hasReliabilityContent,
} from '@/services/analyticsMetrics';
import {
  FirstPartyAnalyticsRange,
  getFirstPartyAnalyticsOverviewComparison,
  getReliabilityAnalytics,
} from '@/services/analyticsService';
import {
  AnalyticsEmptyState,
  AnalyticsErrorState,
  AnalyticsLoadingState,
  AnalyticsPageHeader,
  AnalyticsRefreshControl,
  ChartCard,
  MetricCard,
} from '@/components/analytics/AnalyticsShared';
import { useAnalyticsAutoRefresh } from '@/hooks/useAnalyticsAutoRefresh';
import { ANALYTICS_COLORS, formatDeltaText, formatNumber, formatPercent, formatUtcRange } from '@/components/analytics/analyticsDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLanguage } from '@/i18n';

export default function ReliabilityAnalytics() {
  const [range, setRange] = useState<FirstPartyAnalyticsRange>('7d');
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [previousOverview, setPreviousOverview] = useState<AnalyticsOverview | null>(null);
  const [comparisonWindow, setComparisonWindow] = useState<AnalyticsComparisonWindow | null>(null);
  const [reliability, setReliability] = useState<AnalyticsReliabilitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const loadAnalytics = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const [overviewResult, reliabilityResult] = await Promise.all([
        getFirstPartyAnalyticsOverviewComparison(range),
        getReliabilityAnalytics(range),
      ]);

      setOverview(overviewResult.current);
      setPreviousOverview(overviewResult.previous);
      setComparisonWindow(overviewResult.window);
      setReliability(reliabilityResult);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('analyticsLoadFailed');
      setError(message);
      if (!silent) toast.error(message);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const silentRefresh = useCallback(() => loadAnalytics(true), [loadAnalytics]);
  useAnalyticsAutoRefresh(silentRefresh);

  const hasContent = hasReliabilityContent(reliability) || Boolean((overview?.redirectRequests || 0) > 0 || (overview?.redirectFailures || 0) > 0);
  const requestsByAgent = useMemo(() => reliability?.requestsByAgent.slice(0, 12) || [], [reliability]);
  const failuresByStatus = useMemo(() => reliability?.failuresByStatus.slice(0, 10) || [], [reliability]);
  const comparisonLabel = t('analyticsComparedWithPrior');

  return (
    <div className="space-y-6 animate-fade-in">
      <AnalyticsPageHeader
        title={t('analyticsReliabilityTitle')}
        description={t('analyticsReliabilityDescription')}
        range={range}
        onRangeChange={setRange}
        rangeLabel={t('analyticsDateRange')}
      >
        {overview?.start && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('analyticsUtcRange', { range: formatUtcRange(overview.start, overview.end) })}
          </p>
        )}
        {comparisonWindow?.previous && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t('analyticsPriorUtcRange', { range: formatUtcRange(comparisonWindow.previous.start, comparisonWindow.previous.end) })}
          </p>
        )}
        <p className="mt-3 text-sm text-muted-foreground">
          {t('analyticsReliabilityNote')}
        </p>
        <AnalyticsRefreshControl
          refreshLabel={t('analyticsRefresh')}
          hintLabel={t('analyticsAutoRefreshHint')}
          refreshing={refreshing}
          disabled={loading}
          onRefresh={silentRefresh}
        />
      </AnalyticsPageHeader>

      {loading ? (
        <AnalyticsLoadingState label={t('analyticsLoading')} />
      ) : error ? (
        <AnalyticsErrorState
          title={t('analyticsErrorTitle')}
          message={error}
          retryLabel={t('analyticsRetry')}
          onRetry={() => loadAnalytics()}
        />
      ) : !hasContent || !overview || !reliability ? (
        <AnalyticsEmptyState title={t('analyticsNoEventsTitle')} description={t('analyticsNoReliabilityEventsDescription')} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <MetricCard
              title={t('analyticsBrowserAffiliateClicks')}
              value={formatNumber(overview.affiliateClicks)}
              description={t('analyticsBrowserAffiliateClicksDescription')}
              icon={MousePointerClick}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview.affiliateClicks, previousOverview?.affiliateClicks), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsRedirectRequests')}
              value={formatNumber(overview.redirectRequests)}
              description={t('analyticsRedirectRequestsDescription')}
              icon={Activity}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview.redirectRequests, previousOverview?.redirectRequests), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsRedirectFailures')}
              value={formatNumber(overview.redirectFailures)}
              description={t('analyticsRedirectFailuresDescription')}
              icon={AlertTriangle}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview.redirectFailures, previousOverview?.redirectFailures), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsRedirectFailureRate')}
              value={formatPercent(overview.redirectFailureRate)}
              description={t('analyticsRedirectFailureRateDescription')}
              icon={ShieldAlert}
              comparisonLabel={comparisonLabel}
              comparisonValue={formatDeltaText(compareMetricValues(overview.redirectFailureRate, previousOverview?.redirectFailureRate), {
                newLabel: t('analyticsComparisonNew'),
                noBaselineLabel: t('analyticsComparisonNoBaseline'),
              })}
            />
            <MetricCard
              title={t('analyticsConfirmedPurchases')}
              value={t('analyticsUnavailable')}
              description={t('analyticsConfirmedPurchasesDescription')}
              icon={ShoppingCart}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <ChartCard title={t('analyticsRedirectRequestsByAgent')} description={t('analyticsRedirectRequestsByAgentDescription')}>
              {requestsByAgent.length > 0 ? (
                <div className="h-[320px]" role="img" aria-label={t('analyticsRedirectRequestsByAgent')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={requestsByAgent} margin={{ top: 10, right: 20, bottom: 40, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="dimension" angle={-30} textAnchor="end" height={70} tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value: number) => [formatNumber(value), t('analyticsRedirectRequests')]} />
                      <Bar dataKey="count" name={t('analyticsRedirectRequests')} fill={ANALYTICS_COLORS[4]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <AnalyticsEmptyState title={t('analyticsNoRowsTitle')} description={t('analyticsNoRowsDescription')} />
              )}
            </ChartCard>

            <ChartCard title={t('analyticsRedirectFailuresByStatus')} description={t('analyticsRedirectFailuresByStatusDescription')}>
              {failuresByStatus.length > 0 ? (
                <div className="h-[320px]" role="img" aria-label={t('analyticsRedirectFailuresByStatus')}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={failuresByStatus} layout="vertical" margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                      <YAxis type="category" dataKey="dimension" width={120} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value: number) => [formatNumber(value), t('analyticsRedirectFailures')]} />
                      <Bar dataKey="count" name={t('analyticsRedirectFailures')} fill={ANALYTICS_COLORS[3]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <AnalyticsEmptyState title={t('analyticsNoRowsTitle')} description={t('analyticsNoRowsDescription')} />
              )}
            </ChartCard>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <ChartCard title={t('analyticsRedirectFailuresByAgent')} description={t('analyticsRedirectFailuresByAgentDescription')}>
              <ReliabilityTable
                rows={reliability.failuresByAgent}
                label={t('analyticsAgent')}
                countLabel={t('analyticsRedirectFailures')}
              />
            </ChartCard>
            <ChartCard title={t('analyticsRedirectFailuresByMarketplace')} description={t('analyticsRedirectFailuresByMarketplaceDescription')}>
              <ReliabilityTable
                rows={reliability.failuresByMarketplace}
                label={t('analyticsMarketplace')}
                countLabel={t('analyticsRedirectFailures')}
              />
            </ChartCard>
            <ChartCard title={t('analyticsRedirectFailuresByStage')} description={t('analyticsRedirectFailuresByStageDescription')}>
              <ReliabilityTable
                rows={reliability.failuresByStage}
                label={t('analyticsFailureStage')}
                countLabel={t('analyticsRedirectFailures')}
              />
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
}

function ReliabilityTable({
  rows,
  label,
  countLabel,
}: {
  rows: { dimension: string; count: number }[];
  label: string;
  countLabel: string;
}) {
  const { t } = useLanguage();

  if (rows.length === 0) {
    return <AnalyticsEmptyState title={t('analyticsNoRowsTitle')} description={t('analyticsNoRowsDescription')} />;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{label}</TableHead>
            <TableHead className="text-right">{countLabel}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${label}-${row.dimension}`}>
              <TableCell className="font-medium">{row.dimension}</TableCell>
              <TableCell className="text-right">{formatNumber(row.count)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
